import { gsap } from 'gsap';
import React, { FC, PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Icon } from '@column/column-ui-kit';

import { helpSidebars } from '~/components/HelpSidebar';
import { NotificationList, StyledNotification } from '~/components/NotificationList';
import { useScrollPosition } from '~/hooks';
import { useNavigate } from '~/lib/navigation';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useScrollStore } from '~/stores/Scroll';
import { useSessionStore } from '~/stores/Session';
import { Flex } from '~/styles';

import { Sidebar } from './Sidebar';
import { Events, PlatformStatus, User } from './Toolbar';

interface LocationState {
  from?: string;
}

const Wrapper = styled.div`
  height: 100vh;
  width: 100%;
  box-sizing: border-box;
  flex-grow: 1;
  display: grid;
  grid-template-columns: auto;
  padding-left: 0;
  padding-bottom: max(env(safe-area-inset-bottom), 36px);
  transition:
    padding-left 0.2s ease-in-out,
    border-radius 0.2s ease-in-out;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-left: var(--sidebar-width, 248px);
    padding-bottom: 0;
  }
`;

const Content = styled.div`
  --content-o: 0;
  opacity: var(--content-o);
  min-height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const Toolbar = styled.div<{ $isScrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  padding: 0 var(--page-margin);
`;

const Panel = styled.div`
  padding: 12px 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex: auto;
`;

const FadeIn: FC<PropsWithChildren<{ show?: boolean }>> = ({ children, show }) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    if (show !== undefined) {
      setIsVisible(show);
    }
    if (show === undefined) {
      setIsVisible(true);
    }
  }, [show]);
  return (
    <div
      style={{
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 200ms ease',
      }}
    >
      {children}
    </div>
  );
};

const PageFrame = styled.main`
  position: relative;
  display: flex;
  flex: auto;
  flex-direction: column;
  padding: 0;
  overflow: auto;
  background: ${({ theme }) => theme.background};
  box-shadow:
    0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(1100, 50)},
    0 1px 2px rgba(0, 0, 0, 0.1);
  overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
  margin: 0;
  border-radius: 0;

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      background: ${() => theme.background};
      box-shadow:
        0 0 0 1px ${() => theme.white.blendToBackground(1000, 80)},
        0 1px 2px rgba(0, 0, 0, 0.15);
    `};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin: 0 8px 8px 0;
    border-radius: 8px;
  }
`;

const Inner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledNotificationList = styled(NotificationList)`
  position: fixed;
  bottom: 8px;
  left: 8px;
  padding: 0;
  width: 400px;
  margin: 0;

  ${StyledNotification} {
    margin: 8px 0 0 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    left: calc(var(--sidebar-width) + 12px);
    bottom: 16px;
  }
`;

const LogoLink = styled.button`
  background: none;
  padding: 16px 0;
  outline: none;
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  min-height: 56px;
  z-index: 10;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  transition: opacity 0.2s ease-in-out;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const Layout: FC<PropsWithChildren> = ({ children }) => {
  const setPageFrameScroll = useScrollStore((s) => s.setPageFrameScroll);
  const { openHelpSidebar, openHelpSidebars } = useHelpSidebarStore();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScrollPosition(wrapperRef);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { currentPlatform, currentUser } = useSessionStore();

  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as LocationState;

  useEffect(() => {
    setPageFrameScroll(scrollY);
  }, [scrollY]);

  useEffect(() => {
    if (state && state.from === 'register') {
      openHelpSidebar('Onboarding');
      navigate({ pathname: location.pathname, search: location.search }, { replace: true });
    }
    if (state && state.from === 'auth') {
      gsap.to(sidebarRef.current, {
        '--sidebar-o': 1,
        duration: 0.4,
        ease: 'none',
        onComplete: () => {
          navigate({ pathname: location.pathname, search: location.search }, { replace: true });
        },
      });
      gsap.to(contentRef.current, {
        '--content-o': 1,
        duration: 0.4,
        ease: 'none',
      });
    } else {
      gsap.set(sidebarRef.current, {
        '--sidebar-o': 1,
      });
      gsap.set(contentRef.current, {
        '--content-o': 1,
      });
    }
  }, [location]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    wrapper.scrollTo({ top: 0 });
  }, [wrapperRef, location]);

  return (
    <Wrapper id="scroll-container">
      <Sidebar ref={sidebarRef} isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <Content ref={contentRef}>
        <Toolbar ref={toolbarRef} $isScrolled={scrollY > 0 && openHelpSidebars.length < 1}>
          <LogoLink onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            <Flex alignItems="center" gap={8}>
              <Icon.Hamburger />
            </Flex>
          </LogoLink>

          <Panel>
            {currentPlatform && (
              <FadeIn>
                <Events />
              </FadeIn>
            )}
            {currentPlatform && (
              <FadeIn>
                <PlatformStatus />
              </FadeIn>
            )}

            <FadeIn show={!!currentUser}>
              <User />
            </FadeIn>
          </Panel>
        </Toolbar>
        <PageFrame ref={wrapperRef}>
          <Inner>{children}</Inner>
        </PageFrame>
        <StyledNotificationList display="global" size="large" />
      </Content>

      {currentPlatform && helpSidebars}
    </Wrapper>
  );
};
